/**
 * src/hooks/useAgentAutoBroadcast.ts
 *
 * Drives a short, self-narrated agent broadcast during a cinematic
 * teleport, reusing the EXISTING broadcast stack end-to-end:
 *
 *   - `useAgentBroadcastEngine` renders the agent's avatar canvas into
 *     a `MediaStream` (the consumer mounts the hidden `<AgentBroadcastCanvas>`).
 *   - `requestLiveSession` / `confirmLivePayment` open a real live
 *     session through the SFU (paid sessions honour the operator's
 *     consented `onExecuteLivePayment` handler — we never silently
 *     incur an on-chain charge; if a session needs payment and no
 *     handler is wired, we abort cleanly).
 *   - `startBroadcast` publishes the stream to the SFU room.
 *   - `speakAsAgentDuringLive` synthesises TTS narration server-side
 *     and fans the caption + audio to every viewer (and back to us,
 *     so the avatar lip-syncs / captions render locally too).
 *   - `useBroadcastRecorder` + `uploadBroadcast` record the session
 *     and save it to the agent's profile as a past broadcast.
 *
 * The whole thing is opt-in (the teleport HUD exposes a host-only
 * toggle) and capped to a short duration so it stays a highlight reel,
 * not a long stream.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useAgentBroadcastEngine,
  type AgentBroadcastEngine,
} from '../components/AgentBroadcastCanvas';
import {
  startBroadcast,
  type BroadcastController,
  type AgentCaptionEvent,
} from '../services/webrtcBroadcastService';
import {
  requestLiveSession,
  confirmLivePayment,
  markLivePaymentFailed,
  stopLiveSession,
  speakAsAgentDuringLive,
  type ApproxLocation,
} from '../services/liveSessionService';
import { useBroadcastRecorder } from './useBroadcastRecorder';
import { uploadBroadcast } from '../services/pastBroadcastsService';
import type { ExecuteLivePayment } from '../components/ProfileOverlay';

export type AutoBroadcastStatus =
  | 'idle'
  | 'starting'
  | 'live'
  | 'saving'
  | 'error';

export interface UseAgentAutoBroadcastOpts {
  agentPublicKey: string | null;
  displayName?: string | null;
  avatarUrl?: string | null;
  /** Operator wallet that pays for / owns the session. */
  operatorWallet?: string | null;
  /** Numeric ISO-3166-1 country for the session policy. */
  country?: number | null;
  approxLocation?: ApproxLocation;
  /** Consented wallet payment handler (same one the broadcast modal uses). */
  onExecuteLivePayment?: ExecuteLivePayment;
  /** Build the next narration line. May be async (e.g. an AI call).
   *  Return null/'' to skip this tick. */
  narrate?: () => string | null | Promise<string | null>;
  /** Title for the saved past-broadcast. */
  title?: string | null;
  /** Total broadcast length before auto-stop (default 32s). */
  durationMs?: number;
  /** Cadence between narration lines (default 7s). */
  narrateEveryMs?: number;
}

export interface UseAgentAutoBroadcastResult {
  /** Mount `<AgentBroadcastCanvas engine={engine} hidden />` once. */
  engine: AgentBroadcastEngine;
  status: AutoBroadcastStatus;
  error: string | null;
  /** Transient info message (e.g. "approve the wallet/passkey prompt"). */
  notice: string | null;
  sessionId: string | null;
  viewerCount: number;
  /** Seconds elapsed in the current live broadcast (0 when idle). */
  elapsedSec: number;
  start: () => Promise<void>;
  stop: (reason?: string) => Promise<void>;
}

function base64ToBlob(b64: string, mime: string): Blob {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

/** Stroops → display XLM (trims trailing zeros). */
function stroopsToXlm(stroops: string | number): string {
  const n = Number(stroops || 0) / 1e7;
  if (!Number.isFinite(n)) return '0';
  return n.toFixed(7).replace(/\.?0+$/, '') || '0';
}

/** Pull the backend's machine reason out of a thrown fetch error. */
function reasonOf(e: unknown): string {
  const err = e as Error & { payload?: { reason?: string; error?: string } };
  return err?.payload?.reason || err?.payload?.error || err?.message || 'unknown_error';
}

/** Map a backend live-session reason to an operator-friendly message. */
function friendlyReason(reason: string): string {
  switch (reason) {
    case 'webrtc_policy_not_set':
      return 'Live broadcasts aren\u2019t configured for this country yet. An admin needs to enable the WebRTC policy for the agent\u2019s region.';
    case 'webrtc_disallowed_in_country':
      return 'Live broadcasts are disabled for this country by policy.';
    case 'denied_by_compliance_list':
      return 'This wallet/region is blocked from live broadcasting by the compliance list.';
    case 'auto_broadcast_no_payment_handler':
      return 'This broadcast requires payment but no wallet handler is wired.';
    default:
      return `Couldn\u2019t start broadcast (${reason}).`;
  }
}

// Stop scheduling NEW narration lines this long before the nominal end,
// so the final announcement has runway to finish speaking.
const NARRATION_TAIL_MS = 16_000;
// Hard cap on how long we'll wait past `durationMs` for the last line's
// audio to drain ("a bit over a minute" is fine, a runaway isn't).
const MAX_TAIL_WAIT_MS = 20_000;

export function useAgentAutoBroadcast(
  opts: UseAgentAutoBroadcastOpts,
): UseAgentAutoBroadcastResult {
  const {
    agentPublicKey,
    displayName,
    avatarUrl,
    operatorWallet,
    country,
    approxLocation,
    onExecuteLivePayment,
    narrate,
    title,
    durationMs = 60_000,
    narrateEveryMs = 7_000,
  } = opts;

  const { engine } = useAgentBroadcastEngine({
    agentPublicKey: agentPublicKey || '',
    displayName: displayName ?? null,
    avatarUrl: avatarUrl ?? null,
  });

  const recorder = useBroadcastRecorder({ getStream: () => engine.getStream() });

  const [status, setStatus]           = useState<AutoBroadcastStatus>('idle');
  const [error, setError]             = useState<string | null>(null);
  const [notice, setNotice]           = useState<string | null>(null);
  const [sessionId, setSessionId]     = useState<string | null>(null);
  const [viewerCount, setViewerCount] = useState(0);
  const [elapsedSec, setElapsedSec]   = useState(0);

  const statusRef     = useRef<AutoBroadcastStatus>('idle');
  const controllerRef = useRef<BroadcastController | null>(null);
  const sessionRef    = useRef<{ id: string; grant: string } | null>(null);
  const stopTimerRef  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const narrateTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const elapsedTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Wall-clock time the most recent narration audio is expected to
  // finish playing, and the cutoff after which we stop starting new
  // lines. Used so the cast never ends mid-sentence.
  const lastSpeechEndsAtRef = useRef(0);
  const narrateUntilRef     = useRef(0);
  // Latest opts captured in refs so the long-lived async start()/stop()
  // closures always read current values without re-creating themselves.
  const liveOptsRef = useRef({ narrate, title, onExecuteLivePayment, recorder });
  liveOptsRef.current = { narrate, title, onExecuteLivePayment, recorder };

  useEffect(() => { statusRef.current = status; }, [status]);

  // Recover from a failed start so the chip doesn't latch in 'error'
  // forever. The caller's cooldown (keyed on the last attempt time)
  // prevents this from immediately re-firing into the same failure.
  useEffect(() => {
    if (status !== 'error') return;
    const t = setTimeout(() => {
      setStatus('idle');
      setError(null);
    }, 8000);
    return () => clearTimeout(t);
  }, [status]);

  const clearTimers = useCallback(() => {
    if (stopTimerRef.current)   { clearTimeout(stopTimerRef.current);   stopTimerRef.current = null; }
    if (narrateTimerRef.current){ clearInterval(narrateTimerRef.current); narrateTimerRef.current = null; }
    if (elapsedTimerRef.current){ clearInterval(elapsedTimerRef.current); elapsedTimerRef.current = null; }
  }, []);

  // Decode + push a caption that came back over the room (our own
  // narration is fanned back to us as a `live_agent_caption`).
  const handleCaption = useCallback(async (caption: AgentCaptionEvent) => {
    // Track when this line should finish speaking so the auto-stop can
    // wait for it rather than cutting off mid-sentence.
    const durMs = Number(caption.duration_ms) || 4000;
    lastSpeechEndsAtRef.current = Math.max(lastSpeechEndsAtRef.current, Date.now() + durMs + 400);
    try {
      const blob = base64ToBlob(caption.audio_base64, caption.mime || 'audio/mpeg');
      await engine.pushCaption({ text: caption.text, audio: blob, durationMs: caption.duration_ms });
    } catch {
      try { await engine.pushCaption({ text: caption.text, durationMs: caption.duration_ms }); } catch { /* ignore */ }
    }
  }, [engine]);

  const stop = useCallback(async (_reason?: string) => {
    if (statusRef.current === 'idle' || statusRef.current === 'saving') return;
    statusRef.current = 'saving';
    clearTimers();
    setNotice(null);
    setStatus('saving');
    const sess = sessionRef.current;

    try { controllerRef.current?.stop(); } catch { /* ignore */ }
    controllerRef.current = null;

    // Finalise the recording and upload it to the agent's profile.
    try {
      const rec = await liveOptsRef.current.recorder.stop();
      if (rec && rec.blob.size > 0 && sess) {
        await uploadBroadcast({
          sessionId:   sess.id,
          grantToken:  sess.grant,
          blob:        rec.blob,
          startedAt:   rec.startedAt,
          endedAt:     rec.endedAt,
          durationSec: rec.durationSec,
          title:       liveOptsRef.current.title ?? 'Teleport broadcast',
          events:      rec.events,
        });
      }
    } catch {
      // Recording/upload best-effort — a failed save shouldn't wedge
      // the teleport. The session still gets torn down below.
    }

    if (sess) {
      try { await stopLiveSession(sess.id); } catch { /* server cleans up on socket close */ }
    }
    sessionRef.current = null;
    setSessionId(null);
    setViewerCount(0);
    setElapsedSec(0);
    statusRef.current = 'idle';
    setStatus('idle');
  }, [clearTimers]);

  const start = useCallback(async () => {
    if (statusRef.current !== 'idle') return;
    if (!agentPublicKey || !operatorWallet) {
      setError('Need an operator wallet and agent to broadcast.');
      return;
    }
    // Flip the ref synchronously so a second invocation in the same
    // tick (before React commits 'starting') bails at the guard above
    // — otherwise the trigger effect can fire requestLiveSession twice
    // and the backend rejects the duplicates with 409.
    statusRef.current = 'starting';
    setError(null);
    setStatus('starting');

    let req: Awaited<ReturnType<typeof requestLiveSession>>;
    try {
      req = await requestLiveSession({
        wallet:        operatorWallet,
        broadcasterId: agentPublicKey,
        country:       country == null ? undefined : Number(country),
        approxLocation,
      });
    } catch (e) {
      setError(friendlyReason(reasonOf(e)));
      setStatus('error');
      return;
    }

    let sessId = req.session?.id || '';
    let grant  = req.grantToken;

    if (req.requiresPayment) {
      const pay = liveOptsRef.current.onExecuteLivePayment;
      if (!pay) {
        try { await markLivePaymentFailed(req.session.id, 'auto_broadcast_no_payment_handler'); } catch { /* ignore */ }
        setError(friendlyReason('auto_broadcast_no_payment_handler'));
        setStatus('error');
        return;
      }
      try {
        setNotice(
          `Approve the wallet / passkey prompt to fund the agent\u2019s ~30s live broadcast (${stroopsToXlm(req.sessionCost)} XLM). ` +
          `This pays the live-session fee — nothing else is signed.`,
        );
        const receipt = await pay({
          sessionId:     req.session.id,
          country:       country == null ? null : Number(country),
          wallet:        operatorWallet,
          amountStroops: req.sessionCost,
          amountDisplay: stroopsToXlm(req.sessionCost),
        });
        setNotice(null);
        const confirmed = await confirmLivePayment(req.session.id, {
          paymentReference: receipt.paymentReference,
          paidAmount:       receipt.paidAmount || req.sessionCost,
        });
        sessId = confirmed.session.id;
        grant  = confirmed.grantToken;
      } catch (e) {
        setNotice(null);
        try { await markLivePaymentFailed(req.session.id, reasonOf(e)); } catch { /* ignore */ }
        setError(`Payment failed: ${reasonOf(e)}`);
        setStatus('error');
        return;
      }
    }

    try {
      const stream = engine.getStream();
      const ctrl = startBroadcast(stream, {
        broadcasterId:   agentPublicKey,
        wallet:          operatorWallet || undefined,
        country:         country == null ? undefined : Number(country),
        liveSessionId:   sessId,
        grantToken:      grant,
        onViewersUpdate: (n) => setViewerCount(n),
        onAgentCaption:  (caption) => { void handleCaption(caption); },
        onError:         () => { void stop('broadcast_error'); },
        onSessionEnded:  () => { void stop('session_ended'); },
      });
      controllerRef.current = ctrl;
      sessionRef.current    = { id: sessId, grant };
      setSessionId(sessId);
      liveOptsRef.current.recorder.start();
      setStatus('live');

      // Narration: speak an opening line immediately, then on a
      // cadence until the auto-stop fires. narrate() may be async (the
      // AI assistant call) — we await it but never let a slow/failed
      // line wedge the loop.
      const startedMs = Date.now();
      lastSpeechEndsAtRef.current = startedMs;
      // Don't START a new line in the final stretch — let the last one
      // breathe. The arrival line (first call) always fires.
      narrateUntilRef.current = startedMs + Math.max(0, durationMs - NARRATION_TAIL_MS);

      const speakNext = async (isFirst: boolean) => {
        if (statusRef.current !== 'live') return;
        if (!isFirst && Date.now() > narrateUntilRef.current) return;
        let text: string | null = null;
        try { text = (await liveOptsRef.current.narrate?.()) ?? null; }
        catch { text = null; }
        if (!text || statusRef.current !== 'live') return;
        // Optimistic: assume the line plays for at least a few seconds so
        // the auto-stop holds even before the caption fans back to us.
        lastSpeechEndsAtRef.current = Math.max(lastSpeechEndsAtRef.current, Date.now() + 4500);
        void speakAsAgentDuringLive(sessId, { grantToken: grant, text }).catch(() => { /* ignore */ });
      };
      void speakNext(true);
      narrateTimerRef.current = setInterval(() => { void speakNext(false); }, narrateEveryMs);

      // Elapsed ticker for the HUD.
      elapsedTimerRef.current = setInterval(() => {
        setElapsedSec(Math.round((Date.now() - startedMs) / 1000));
      }, 1000);

      // At the nominal end, wait for any in-flight narration to finish
      // (capped) so the cast never ends mid-sentence — it may run a bit
      // over or under a minute as a result.
      const finishWhenQuiet = async () => {
        if (statusRef.current !== 'live') return;
        const remaining = lastSpeechEndsAtRef.current - Date.now();
        if (remaining > 0) {
          await new Promise((r) => setTimeout(r, Math.min(remaining, MAX_TAIL_WAIT_MS)));
        }
        void stop('duration');
      };
      stopTimerRef.current = setTimeout(() => { void finishWhenQuiet(); }, durationMs);
    } catch (e) {
      setError(`failed to start broadcast: ${(e as Error).message}`);
      setStatus('error');
      try { await stopLiveSession(sessId); } catch { /* ignore */ }
      sessionRef.current = null;
    }
  }, [agentPublicKey, operatorWallet, country, approxLocation, engine, handleCaption, narrateEveryMs, durationMs, stop]);

  // Tear down on unmount.
  useEffect(() => () => {
    clearTimers();
    try { controllerRef.current?.stop(); } catch { /* ignore */ }
    controllerRef.current = null;
    const sess = sessionRef.current;
    if (sess) { void stopLiveSession(sess.id).catch(() => { /* ignore */ }); }
  }, [clearTimers]);

  return { engine, status, error, notice, sessionId, viewerCount, elapsedSec, start, stop };
}
